import { useState, useCallback } from "react";
import { useApolloClient } from "@apollo/client";
import { Download } from "lucide-react";
import { EXPORT_ORGANIZATION_ROSTER } from "../graphql";
import { exportToCsv, buildExportFilename } from "../utils/exportCsv";
import type { CsvColumn } from "../utils/exportCsv";
import type { RosterEntry, RosterEnrollmentStatus, RosterResult } from "../types";
import { useLocale } from "../../../context/LanguageContext";

/** Backend max per page for organizationRoster (@Max(100) on input). */
const BATCH_SIZE = 100;
/** Safety cap to prevent runaway loops. */
const MAX_ROWS = 10_000;

interface ExportRosterButtonProps {
  organizationId: string;
  orgSlug: string;
  /** Current status filter — export respects the active filter */
  statusFilter: RosterEnrollmentStatus | null;
}

const ENROLLMENT_STATUS_LABEL_KEYS: Record<RosterEnrollmentStatus, string> = {
  ACTIVE: "roster.statusActive",
  PENDING: "roster.statusPending",
  NOT_INVITED: "roster.statusNotInvited",
};

function buildRosterColumns(
  t: (key: string, params?: Record<string, string | number>) => string,
): CsvColumn<RosterEntry>[] {
  return [
    { header: t("roster.export.colFirstName"), accessor: (r) => r.firstName ?? "" },
    { header: t("roster.export.colLastName"), accessor: (r) => r.lastName ?? "" },
    { header: t("roster.export.colEmail"), accessor: (r) => r.workEmail },
    { header: t("roster.export.colDepartment"), accessor: (r) => r.department ?? "" },
    { header: t("roster.export.colJobTitle"), accessor: (r) => r.jobTitle ?? "" },
    { header: t("roster.export.colEmployeeId"), accessor: (r) => r.employeeId ?? "" },
    { header: t("roster.export.colEmployeeStatus"), accessor: (r) => r.employeeStatus ?? "" },
    {
      header: t("roster.export.colEnrollmentStatus"),
      accessor: (r) =>
        ENROLLMENT_STATUS_LABEL_KEYS[r.enrollmentStatus]
          ? t(ENROLLMENT_STATUS_LABEL_KEYS[r.enrollmentStatus])
          : r.enrollmentStatus,
    },
    {
      header: t("roster.export.colAddedOn"),
      accessor: (r) => r.createdAt ? r.createdAt.slice(0, 10) : "",
    },
  ];
}

export function ExportRosterButton({
  organizationId,
  orgSlug,
  statusFilter,
}: ExportRosterButtonProps) {
  const [exporting, setExporting] = useState(false);
  const client = useApolloClient();
  const { t } = useLocale();

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const allItems: RosterEntry[] = [];
      let offset = 0;
      let hasMore = true;

      while (hasMore && allItems.length < MAX_ROWS) {
        const { data } = await client.query<{ organizationRoster: RosterResult }>({
          query: EXPORT_ORGANIZATION_ROSTER,
          variables: {
            organizationId,
            input: {
              limit: BATCH_SIZE,
              offset,
              ...(statusFilter ? { status: statusFilter } : {}),
            },
          },
          fetchPolicy: "network-only",
        });

        const page = data?.organizationRoster;
        if (!page?.items?.length) break;

        allItems.push(...page.items);
        offset += BATCH_SIZE;
        hasMore = page.items.length === BATCH_SIZE;
      }

      if (allItems.length === 0) {
        console.warn("[ExportRosterButton] No data to export");
        return;
      }

      exportToCsv({
        filename: buildExportFilename(orgSlug, "roster"),
        columns: buildRosterColumns(t),
        rows: allItems,
      });
    } catch (err) {
      console.error("[ExportRosterButton] Export failed:", err);
    } finally {
      setExporting(false);
    }
  }, [client, organizationId, orgSlug, statusFilter, t]);

  return (
    <button
      type="button"
      onClick={handleExport}
      disabled={exporting}
      className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
    >
      {exporting ? (
        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-foreground border-t-transparent" />
      ) : (
        <Download className="h-3.5 w-3.5" />
      )}
      {t("common.exportCsv")}
    </button>
  );
}
